import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { apiService } from '../services/api';

interface TranslationProgressProps {
  translationId: string;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

const TranslationProgress: React.FC<TranslationProgressProps> = ({ 
  translationId, 
  onComplete, 
  onError 
}) => {
  const [progress, setProgress] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    let timeout: NodeJS.Timeout | null = null;

    const checkProgress = async () => {
      try {
        const response = await apiService.getTranslationProgress(translationId);
        if (response.success && response.data) {
          setProgress(response.data);
          
          if (response.data.status === 'completed') {
            onComplete?.();
            // Stop polling immediately when completed
            if (interval) {
              clearInterval(interval);
              interval = null;
            }
            // Auto-hide after 3 seconds
            setTimeout(() => setIsVisible(false), 3000);
          } else if (response.data.status === 'failed') {
            onError?.(response.data.error || 'Translation failed');
            // Stop polling immediately when failed
            if (interval) {
              clearInterval(interval);
              interval = null;
            }
            // Auto-hide after 5 seconds
            setTimeout(() => setIsVisible(false), 5000);
          }
        }
      } catch (error) {
        console.error('Error checking translation progress:', error);
        // If we can't fetch progress, assume it's completed and hide
        setTimeout(() => setIsVisible(false), 10000);
      }
    };

    // Check progress immediately
    checkProgress();

    // Set up polling every 2 seconds, but stop after 10 minutes
    interval = setInterval(checkProgress, 2000);
    timeout = setTimeout(() => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    }, 10 * 60 * 1000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [translationId, onComplete, onError]);

  if (!isVisible || !progress) return null;

  const getStatusIcon = () => {
    switch (progress.status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
        );
    }
  };

  const getStatusColor = () => {
    switch (progress.status) {
      case 'completed':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'failed':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  return (
    <div className={`fixed top-4 right-4 z-50 max-w-sm p-4 border rounded-lg shadow-lg ${getStatusColor()}`}>
      <div className="flex items-start space-x-3">
        {getStatusIcon()}
        <div className="flex-1">
          <h3 className="text-sm font-medium">
            {progress.status === 'completed' ? 'Translation Complete!' : 
             progress.status === 'failed' ? 'Translation Failed' : 
             'Translating Tutorial...'}
          </h3>
          <p className="text-sm mt-1">{progress.message}</p>
          
          {progress.status === 'processing' && (
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress.progress}%` }}
                ></div>
              </div>
              <p className="text-xs mt-1">{progress.progress}% complete</p>
            </div>
          )}
          
          {progress.error && (
            <p className="text-xs mt-1 text-red-600">{progress.error}</p>
          )}
        </div>
        
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          <span className="sr-only">Close</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default TranslationProgress;
